import React, { useEffect, useMemo, useState } from 'react';
import { Bubble, Input, Button, InputProps } from 'tea-component';
import classNames from 'classnames';
import useLanguage from '@op/hooks/useLanguage';
import '../scss/range.scss';
import './index.scss';
import SvgIcon from '@home/components/SvgIcon';

const DEFAULT_REGEX = /^\d+(\.\d{1,3})?$/;

export default function CustomInput({
  unit = 'K',
  unitPosition = 'suffix',
  maxValue,
  minValue,
  maxErrorText,
  minErrorText,
  errorTextFormatted = false,
  regex = DEFAULT_REGEX,
  onConfirm,
  onBack,
  emptyErrorText = 'Please enter numbers, symbols are not supported.',
  valueErrorText = 'Please enter a numerical value to three decimal places.',
  backText = 'Back',
  rangeValue,
  showClear = false,
  inputProps = {},
}: {
  /** 单位符号（如 K、M），默认 'K' */
  unit?: string;
  /** 单位位置：'suffix' 后缀（默认） | 'prefix' 前缀 */
  unitPosition?: 'prefix' | 'suffix';
  /** 最大值限制 */
  maxValue?: number;
  /** 最小值限制 */
  minValue?: number;
  /** 超过最大值时的自定义错误提示文案 */
  maxErrorText?: string;
  /** 低于最小值时的自定义错误提示文案 */
  minErrorText?: string;
  emptyErrorText?: string;
  valueErrorText?: string;
  /** 标记 maxErrorText / minErrorText 是否已经过 formatMessage 处理，默认 false */
  errorTextFormatted?: boolean;
  /** 输入值格式校验正则，默认 /^\d+(\.\d{1,3})?$/ */
  regex?: RegExp;
  /** 返回按钮文案，支持 intl key，默认 'Back' */
  backText?: string;
  /** 回显的当前范围值（格式："start-end"），用于打开时回填输入框 */
  rangeValue?: string;
  /** 是否显示清除按钮，默认 false */
  showClear?: boolean;
  /** 确认回调，返回起始值和结束值 */
  onConfirm: (startRange: string, endRange: string) => void;
  /** 返回选下面板 */
  onBack: () => void;
  inputProps?: InputProps & React.RefAttributes<HTMLInputElement>;
}) {
  const { formatMessage } = useLanguage();

  const getInitialRange = () => {
    if (!rangeValue) return ['', ''];
    return rangeValue.split('-');
  };

  const [inputStartErrorText, setInputStartErrorText] = useState<string | null>(
    null,
  );
  const [inputEndErrorText, setInputEndErrorText] = useState<string | null>(
    null,
  );
  const [startRange, setStartRange] = useState(getInitialRange()[0]);
  const [endRange, setEndRange] = useState(getInitialRange()[1]);

  // rangeValue 变化时同步回填输入框
  useEffect(() => {
    const parts = rangeValue ? rangeValue.split('-') : ['', ''];
    setStartRange(parts[0] || '');
    setEndRange(parts[1] || '');
  }, [rangeValue]);

  function getErrorMessage(isSimple: boolean = true) {
    let startErrorText: string | null = null;
    let endErrorText: string | null = null;
    const empty = formatMessage(emptyErrorText);
    const valueError = formatMessage(valueErrorText);
    // 外部传入的文案可能已翻译（errorTextFormatted=true）或是 intl key，分别处理
    const maxError = errorTextFormatted
      ? maxErrorText
      : formatMessage(
          maxErrorText || `The entered value cannot exceed ${maxValue}${unit}.`,
        );
    const minError = errorTextFormatted
      ? minErrorText
      : formatMessage(
          minErrorText ||
            `The entered value cannot be less than ${minValue}${unit}.`,
        );

    if (startRange !== '' && !regex.test(startRange)) {
      startErrorText = valueError;
    }

    if (endRange !== '' && !regex.test(endRange)) {
      endErrorText = valueError;
    }

    if (!isSimple) {
      const start = Number(startRange),
        end = Number(endRange);
      if (start < 0 || isNaN(start) || end < 0 || isNaN(end)) {
        startErrorText = empty;
        endErrorText = empty;
      }
    }

    // 最大值校验 — 格式通过后检查数值范围
    if (maxValue !== undefined) {
      if (startRange !== '' && !startErrorText) {
        const startNum = Number(startRange);
        if (startNum > maxValue) {
          startErrorText = maxError || null;
        }
      }
      if (endRange !== '' && !endErrorText) {
        const endNum = Number(endRange);
        if (endNum > maxValue) {
          endErrorText = maxError || null;
        }
      }
    }

    // 最小值校验
    if (minValue !== undefined) {
      if (startRange !== '' && !startErrorText) {
        const startNum = Number(startRange);
        if (startNum < minValue) {
          startErrorText = minError || null;
        }
      }
      if (endRange !== '' && !endErrorText) {
        const endNum = Number(endRange);
        if (endNum < minValue) {
          endErrorText = minError || null;
        }
      }
    }

    return {
      startErrorText,
      endErrorText,
      isFalse: !!startErrorText || !!endErrorText,
    };
  }

  function changeInputState(isSimple: boolean = true) {
    const { startErrorText, endErrorText, isFalse } = getErrorMessage(isSimple);

    setInputStartErrorText(startErrorText);
    setInputEndErrorText(endErrorText);
    return isFalse;
  }

  useEffect(() => {
    changeInputState();
  }, [startRange, endRange]);

  const btnDisabled = useMemo(() => {
    const { isFalse } = getErrorMessage(false);
    return isFalse;
  }, [startRange, endRange]);

  function checkInput() {
    if (changeInputState(false)) return;
    // 两个输入框都为空 → 全选
    if (startRange === '' && endRange === '') {
      onConfirm('', '');
      setInputStartErrorText(null);
      setInputEndErrorText(null);
      return;
    }
    // 如果一侧为空且 minValue 存在，则补为最小值
    const finalStart =
      startRange === '' && minValue !== undefined
        ? String(minValue)
        : startRange;
    const finalEnd =
      endRange === '' && minValue !== undefined ? String(minValue) : endRange;
    onConfirm(finalStart, finalEnd);
    setInputStartErrorText(null);
    setInputEndErrorText(null);
  }

  const inputConfigs: {
    value: string;
    onChange: (value: string) => void;
    errorText: string | null;
    placement: 'left' | 'right';
  }[] = [
    {
      value: startRange,
      onChange: setStartRange,
      errorText: inputStartErrorText,
      placement: 'left',
    },
    {
      value: endRange,
      onChange: setEndRange,
      errorText: inputEndErrorText,
      placement: 'right',
    },
  ];

  return (
    <div className="dbo-followerfilter-input-wrap">
      <div className="dbo-followerfilter-input">
        {inputConfigs.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <span className="dbo-followerfilter-input-gap">-</span>
            )}
            <Bubble
              error
              content={item.errorText}
              visible={!!item.errorText}
              placement={item.placement}
            >
              <div
                className={classNames('dbo-followerfilter-input-item', {
                  error: !!item.errorText,
                })}
              >
                {unit && unitPosition === 'prefix' && (
                  <div className="dbo-followerfilter-input-unit --prefix">
                    {unit}
                  </div>
                )}

                <Input
                  className={classNames('dbo-followerfilter-input', {
                    '--has-prefix': unit && unitPosition === 'prefix',
                    '--has-suffix': unit && unitPosition === 'suffix',
                  })}
                  value={item.value}
                  onChange={item.onChange}
                  {...inputProps}
                />
                {showClear && item.value && (
                  <span
                    className={classNames('dbo-followerfilter-input-clear', {
                      '--has-unit-suffix': unit && unitPosition === 'suffix',
                    })}
                    onClick={() => item.onChange('')}
                  >
                    <SvgIcon type="clear-icon" />
                  </span>
                )}
                {unit && unitPosition === 'suffix' && (
                  <div className="dbo-followerfilter-input-unit">{unit}</div>
                )}
              </div>
            </Bubble>
          </React.Fragment>
        ))}
      </div>
      <div className="dbo-followerfilter-btngroup">
        <Button
          disabled={btnDisabled}
          type="primary"
          onClick={() => checkInput()}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={onBack}>
          {formatMessage({ id: backText })}
        </Button>
      </div>
    </div>
  );
}
